import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, X, Edit2, Trash2, Star, Loader2, Upload,
  CheckCircle, Package, Eye, EyeOff, ChevronDown, Image, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["bouquet", "arrangement", "dried", "preserved", "subscription", "custom"];
const OCCASIONS = ["love", "anniversary", "birthday", "wedding", "gratitude", "healing", "mother", "graduation", "sympathy", "congratulations"];
const COLORS_LIST = ["red", "pink", "white", "yellow", "purple", "orange", "blue", "green", "cream", "coral", "lavender"];

const EMPTY_FORM = {
  name_en: "", name_id: "",
  description_en: "", description_id: "",
  story_en: "", story_id: "",
  price: "", category: "bouquet",
  occasion: [], colors: [],
  style: "", size: "medium",
  images: [], is_featured: false,
  is_memory_bouquet: false, status: "active",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [modal, setModal] = useState(null); // null | "create" | product_obj
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [modalTab, setModalTab] = useState("details"); // "details" | "bom"
  const [flowersInventory, setFlowersInventory] = useState([]);
  const [bomEntries, setBomEntries] = useState([]); // [{flower_code, flower_name, qty, unit}]
  const [loadingBom, setLoadingBom] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const [pRes, fRes] = await Promise.all([
        axios.get(`${API}/products?limit=200`),
        axios.get(`${API}/admin/inventory/flowers`, { withCredentials: true }).catch(() => ({ data: { flowers: [] } }))
      ]);
      setProducts(pRes.data.products || []);
      setFlowersInventory(fRes.data.flowers || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const filtered = products.filter(p => {
    const matchSearch = !search ||
      p.name_en?.toLowerCase().includes(search.toLowerCase()) ||
      p.name_id?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || p.status === filterStatus;
    const matchCat = filterCategory === "all" || p.category === filterCategory;
    return matchSearch && matchStatus && matchCat;
  });

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setNewImageUrl("");
    setModal("create");
  };

  const openEdit = (product) => {
    setForm({
      name_en: product.name_en || "",
      name_id: product.name_id || "",
      description_en: product.description_en || "",
      description_id: product.description_id || "",
      story_en: product.story_en || "",
      story_id: product.story_id || "",
      price: product.price || "",
      category: product.category || "bouquet",
      occasion: product.occasion || [],
      colors: product.colors || [],
      style: product.style || "",
      size: product.size || "medium",
      images: product.images || (product.image_url ? [product.image_url] : []),
      is_featured: product.is_featured || false,
      is_memory_bouquet: product.is_memory_bouquet || false,
      status: product.status || "active",
    });
    setNewImageUrl("");
    setModal(product);
    setModalTab("details");
    loadBomForProduct(product.product_id);
  };

  const loadBomForProduct = async (productId) => {
    setLoadingBom(true);
    try {
      const res = await axios.get(`${API}/admin/products/${productId}/bom`, { withCredentials: true });
      setBomEntries(res.data.flowers_bom || []);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Error loading BOM:", err);
      }
      setBomEntries([]);
    } finally {
      setLoadingBom(false);
    }
  };

  const saveBom = async () => {
    if (!modal || modal === "create") return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${modal.product_id}/bom`, {
        flowers_bom: bomEntries
      }, { withCredentials: true });
      toast.success("BOM berhasil disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan BOM");
    } finally {
      setSaving(false);
    }
  };

  const addBomEntry = (flowerCode) => {
    const flower = flowersInventory.find(f => f.flower_code === flowerCode);
    if (flower && !bomEntries.find(b => b.flower_code === flowerCode)) {
      setBomEntries(prev => [...prev, {
        flower_code: flower.flower_code,
        flower_name: flower.flower_name || flower.flower_code,
        qty: 1,
        unit: "tangkai"
      }]);
    }
  };

  const removeBomEntry = (index) => {
    setBomEntries(prev => prev.filter((_, i) => i !== index));
  };

  const updateBomQty = (index, qty) => {
    setBomEntries(prev => prev.map((entry, i) => i === index ? { ...entry, qty: parseFloat(qty) || 0 } : entry));
  };

  const handleSave = async () => {
    if (!form.name_en || !form.name_id || !form.price) {
      toast.error("Name (EN/ID) and price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (modal === "create") {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
        toast.success("Product created!");
      } else {
        await axios.put(`${API}/admin/products/${modal.product_id}`, payload, { withCredentials: true });
        toast.success("Product updated!");
      }
      setModal(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Archive "${product.name_en}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.product_id}`, { withCredentials: true });
      toast.success("Product archived");
      fetchProducts();
    } catch { toast.error("Failed to archive"); }
  };

  const toggleFeatured = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.product_id}`, { is_featured: !product.is_featured }, { withCredentials: true });
      fetchProducts();
    } catch { toast.error("Failed"); }
  };

  const toggleStatus = async (product) => {
    const next = product.status === "active" ? "archived" : "active";
    try {
      await axios.put(`${API}/admin/products/${product.product_id}`, { status: next }, { withCredentials: true });
      toast.success(next === "active" ? "Product activated" : "Product archived");
      fetchProducts();
    } catch { toast.error("Failed"); }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(prev => ({ ...prev, images: [...prev.images, url] }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploadingImg(false);
  };

  const addImageUrl = () => {
    if (!newImageUrl.trim()) return;
    setForm(prev => ({ ...prev, images: [...prev.images, newImageUrl.trim()] }));
    setNewImageUrl("");
  };

  const removeImage = (idx) => setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const toggleOccasion = (occ) => setForm(prev => ({
    ...prev,
    occasion: prev.occasion.includes(occ) ? prev.occasion.filter(o => o !== occ) : [...prev.occasion, occ]
  }));
  const toggleColor = (col) => setForm(prev => ({
    ...prev,
    colors: prev.colors.includes(col) ? prev.colors.filter(c => c !== col) : [...prev.colors, col]
  }));

  return (
    <div data-testid="admin-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-[#1C1A19]">Products</h1>
          <p className="font-body text-sm text-[#8A857D] mt-1">{products.length} total · {products.filter(p => p.status === "active").length} active</p>
        </div>
        <button onClick={openCreate} data-testid="create-product-btn" className="btn-pill btn-pill--solid gap-2">
          <Plus size={14} /> New Product
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29A]" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="pl-8 pr-4 py-2 rounded-xl bg-white border border-[#D4859B]/20 text-sm font-body text-[#1C1A19] placeholder:text-[#A8A29A] focus:outline-none focus:border-seikat-pink/50 w-56"
            data-testid="products-search"
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-xl bg-white border border-[#D4859B]/20 text-sm font-body text-[#4A453F] focus:outline-none">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-3 py-2 rounded-xl bg-white border border-[#D4859B]/20 text-sm font-body text-[#4A453F] focus:outline-none">
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-seikat-pink" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Package size={40} className="mx-auto text-[#D4859B]/40 mb-3" />
          <p className="font-body text-[#A8A29A]">No products found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="border-b border-[#D4859B]/10 bg-[#FDF8F6]">
                <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Product</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Category</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Price</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Status</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Featured</th>
                <th className="text-right px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D4859B]/8">
              {filtered.map((p) => (
                <tr key={p.product_id} className="hover:bg-[#FDF8F6]/50 transition-colors" data-testid={`product-row-${p.product_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F5EDE8] flex-shrink-0">
                        {p.images?.[0] || p.image_url ? (
                          <img src={p.images?.[0] || p.image_url} alt={p.name_en} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Image size={14} className="text-[#D4859B]/50" /></div>
                        )}
                      </div>
                      <div>
                        <p className="font-body text-sm font-medium text-[#1C1A19] truncate max-w-[180px]">{p.name_en}</p>
                        <p className="font-body text-xs text-[#A8A29A] truncate max-w-[180px]">{p.name_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-body text-xs bg-[#D4859B]/12 text-[#B07C9B] px-2.5 py-1 rounded-full">{p.category}</span>
                  </td>
                  <td className="px-4 py-3 font-body text-sm font-medium text-[#1C1A19]">
                    Rp {p.price?.toLocaleString("id-ID")}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleStatus(p)} className={`font-body text-xs px-2.5 py-1 rounded-full transition-colors ${
                      p.status === "active"
                        ? "bg-[#C5DB7B]/20 text-[#5A7A1A] hover:bg-[#C5DB7B]/30"
                        : "bg-[#A8A29A]/15 text-[#6A6560] hover:bg-[#A8A29A]/25"
                    }`}>
                      {p.status === "active" ? "Active" : "Archived"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleFeatured(p)} className={`transition-colors ${
                      p.is_featured ? "text-[#D4859B]" : "text-[#D4859B]/25 hover:text-[#D4859B]/60"
                    }`} data-testid={`feature-toggle-${p.product_id}`}>
                      <Star size={16} fill={p.is_featured ? "#D4859B" : "none"} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <a href={`/product/${p.product_id}`} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg hover:bg-[#D4859B]/12 flex items-center justify-center text-[#A8A29A] hover:text-[#D4859B] transition-colors">
                        <ExternalLink size={13} />
                      </a>
                      <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`} className="w-7 h-7 rounded-lg hover:bg-[#D4859B]/12 flex items-center justify-center text-[#A8A29A] hover:text-[#D4859B] transition-colors">
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleDelete(p)} className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#A8A29A] hover:text-red-400 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#1C1A19]/50 backdrop-blur-sm flex items-start justify-end"
            onClick={e => { if (e.target === e.currentTarget) setModal(null); }}
          >
            <motion.div
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="w-full max-w-2xl h-screen overflow-y-auto bg-white shadow-2xl"
              data-testid="product-modal"
            >
              <div className="sticky top-0 bg-white border-b border-[#D4859B]/12 z-10">
                <div className="px-7 py-5 flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-xl text-[#1C1A19]">{modal === "create" ? "New Product" : `Edit: ${modal.name_en}`}</h2>
                    <p className="font-body text-xs text-[#A8A29A] mt-0.5">All fields with * are required</p>
                  </div>
                  <button onClick={() => setModal(null)} className="w-8 h-8 rounded-full hover:bg-[#D4859B]/10 flex items-center justify-center text-[#A8A29A]">
                    <X size={16} />
                  </button>
                </div>
                
                {/* Tabs - only show for edit mode */}
                {modal !== "create" && (
                  <div className="flex gap-1 px-7 bg-[#FDF8F6]">
                    <button onClick={() => setModalTab("details")} data-testid="tab-details"
                      className={`px-4 py-2.5 text-sm font-body transition-colors border-b-2 ${
                        modalTab === "details" 
                          ? "border-seikat-pink text-seikat-pink font-medium" 
                          : "border-transparent text-[#8A857D] hover:text-[#1C1A19]"
                      }`}>
                      📝 Product Details
                    </button>
                    <button onClick={() => setModalTab("bom")} data-testid="tab-bom"
                      className={`px-4 py-2.5 text-sm font-body transition-colors border-b-2 ${
                        modalTab === "bom" 
                          ? "border-seikat-pink text-seikat-pink font-medium" 
                          : "border-transparent text-[#8A857D] hover:text-[#1C1A19]"
                      }`}>
                      🌸 BOM (Bill of Materials)
                      {bomEntries.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-seikat-pink/15 text-seikat-pink rounded text-xs">{bomEntries.length}</span>}
                    </button>
                  </div>
                )}
              </div>

              {/* Tab Content */}
              {modalTab === "details" ? (
              <div className="px-7 py-6 space-y-6">
                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Name (English) *">
                    <input value={form.name_en} onChange={e => setForm(p => ({...p, name_en: e.target.value}))} placeholder="Eternal Rose Bouquet" className="admin-input" data-testid="product-name-en" />
                  </FormField>
                  <FormField label="Nama (Indonesia) *">
                    <input value={form.name_id} onChange={e => setForm(p => ({...p, name_id: e.target.value}))} placeholder="Bouquet Mawar Abadi" className="admin-input" data-testid="product-name-id" />
                  </FormField>
                </div>

                {/* Description */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Description (EN)">
                    <textarea value={form.description_en} onChange={e => setForm(p => ({...p, description_en: e.target.value}))} rows={3} className="admin-input resize-none" placeholder="English description..." />
                  </FormField>
                  <FormField label="Deskripsi (ID)">
                    <textarea value={form.description_id} onChange={e => setForm(p => ({...p, description_id: e.target.value}))} rows={3} className="admin-input resize-none" placeholder="Deskripsi Indonesia..." />
                  </FormField>
                </div>

                {/* Story */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Story (EN)">
                    <textarea value={form.story_en} onChange={e => setForm(p => ({...p, story_en: e.target.value}))} rows={2} className="admin-input resize-none" placeholder="Poetic story in English..." />
                  </FormField>
                  <FormField label="Cerita (ID)">
                    <textarea value={form.story_id} onChange={e => setForm(p => ({...p, story_id: e.target.value}))} rows={2} className="admin-input resize-none" placeholder="Cerita puitis dalam bahasa Indonesia..." />
                  </FormField>
                </div>

                {/* Price + Category + Size */}
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="Price (Rp) *">
                    <input type="number" value={form.price} onChange={e => setForm(p => ({...p, price: e.target.value}))} placeholder="450000" className="admin-input" data-testid="product-price" />
                  </FormField>
                  <FormField label="Category">
                    <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="admin-input">
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Size">
                    <select value={form.size} onChange={e => setForm(p => ({...p, size: e.target.value}))} className="admin-input">
                      {["small","medium","large","xl"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </FormField>
                </div>

                {/* Occasions */}
                <FormField label="Occasions">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {OCCASIONS.map(occ => (
                      <button key={occ} type="button" onClick={() => toggleOccasion(occ)}
                        className={`px-3 py-1 rounded-full text-xs font-body border transition-all ${
                          form.occasion.includes(occ)
                            ? "bg-seikat-pink text-white border-seikat-pink"
                            : "border-[#D4859B]/30 text-[#8A857D] hover:border-seikat-pink/50"
                        }`}>{occ}
                      </button>
                    ))}
                  </div>
                </FormField>

                {/* Colors */}
                <FormField label="Colors">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {COLORS_LIST.map(col => (
                      <button key={col} type="button" onClick={() => toggleColor(col)}
                        className={`px-3 py-1 rounded-full text-xs font-body border transition-all ${
                          form.colors.includes(col)
                            ? "bg-[#C5DB7B] text-[#1C1A19] border-[#C5DB7B]"
                            : "border-[#D4859B]/30 text-[#8A857D] hover:border-[#C5DB7B]/50"
                        }`}>{col}
                      </button>
                    ))}
                  </div>
                </FormField>

                {/* Style */}
                <FormField label="Style">
                  <input value={form.style} onChange={e => setForm(p => ({...p, style: e.target.value}))} placeholder="romantic, elegant, minimal..." className="admin-input" />
                </FormField>

                {/* Images */}
                <FormField label="Product Images">
                  <div className="space-y-3">
                    {form.images.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {form.images.map((url, i) => (
                          <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#D4859B]/20">
                            <img src={url} alt={`img-${i}`} className="w-full h-full object-cover" />
                            {i === 0 && <span className="absolute top-1 left-1 text-[9px] bg-seikat-pink text-white px-1.5 py-0.5 rounded-full">Main</span>}
                            <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600">
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addImageUrl()}
                        placeholder="Paste image URL..."
                        className="admin-input flex-1"
                      />
                      <button onClick={addImageUrl} className="btn-pill text-xs px-3">Add URL</button>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer btn-pill text-xs w-fit">
                      {uploadingImg ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                      Upload Image
                      <input type="file" accept="image/*" className="hidden" onChange={handleUploadImage} disabled={uploadingImg} />
                    </label>
                  </div>
                </FormField>

                {/* Toggles */}
                <div className="flex gap-6">
                  <Toggle label="Featured" checked={form.is_featured} onChange={v => setForm(p => ({...p, is_featured: v}))} />
                  <Toggle label="Memory Bouquet AI" checked={form.is_memory_bouquet} onChange={v => setForm(p => ({...p, is_memory_bouquet: v}))} />
                  <div className="flex items-center gap-2">
                    <span className="font-body text-xs text-[#4A453F]">Status</span>
                    <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="admin-input text-xs py-1.5 px-2">
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>
              ) : (
                /* BOM Tab */
                <div className="px-7 py-6 space-y-6">
                  <div className="bg-[#FDF8F6] rounded-xl p-5 mb-5">
                    <h3 className="font-heading text-base text-[#1C1A19] mb-2">Bill of Materials (BOM)</h3>
                    <p className="font-body text-sm text-[#8A857D]">
                      BOM mendefinisikan komposisi bahan baku (flowers_master) yang dibutuhkan untuk membuat 1 unit produk ini. 
                      Saat POS order dibuat, inventory akan otomatis berkurang sesuai BOM.
                    </p>
                  </div>

                  {loadingBom ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="animate-spin text-seikat-pink" size={24} />
                    </div>
                  ) : (
                    <>
                      {/* BOM Entries Table */}
                      {bomEntries.length > 0 && (
                        <div className="bg-white border border-[#D4859B]/12 rounded-xl overflow-hidden mb-4">
                          <table className="w-full">
                            <thead className="bg-[#FDF8F6]">
                              <tr>
                                <th className="text-left px-4 py-2.5 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Flower</th>
                                <th className="text-left px-4 py-2.5 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Qty</th>
                                <th className="text-left px-4 py-2.5 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Unit</th>
                                <th className="w-16 px-4 py-2.5"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#D4859B]/8">
                              {bomEntries.map((entry, idx) => (
                                <tr key={idx} className="hover:bg-[#FDF8F6]/50" data-testid={`bom-entry-${idx}`}>
                                  <td className="px-4 py-2.5 font-body text-sm text-[#1C1A19]">{entry.flower_name}</td>
                                  <td className="px-4 py-2.5">
                                    <input type="number" min="0" step="0.5" value={entry.qty} 
                                      onChange={e => updateBomQty(idx, e.target.value)}
                                      className="w-20 px-2 py-1 border border-[#D4859B]/20 rounded-lg text-sm text-center" 
                                      data-testid={`bom-qty-${idx}`} />
                                  </td>
                                  <td className="px-4 py-2.5 font-body text-sm text-[#8A857D]">{entry.unit}</td>
                                  <td className="px-4 py-2.5">
                                    <button onClick={() => removeBomEntry(idx)} data-testid={`bom-remove-${idx}`}
                                      className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#A8A29A] hover:text-red-500 transition-colors">
                                      <Trash2 size={13} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Add Flower Dropdown */}
                      <div>
                        <label className="block text-[10px] tracking-widest uppercase text-[#A8A29A] mb-2">Tambah Bunga ke BOM</label>
                        <select onChange={e => { if (e.target.value) { addBomEntry(e.target.value); e.target.value = ""; }}}
                          className="w-full px-3 py-2.5 bg-white border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50"
                          data-testid="bom-flower-select">
                          <option value="">-- Pilih Bunga --</option>
                          {flowersInventory.map(f => (
                            <option key={f.flower_code} value={f.flower_code} 
                              disabled={bomEntries.some(b => b.flower_code === f.flower_code)}>
                              {f.flower_name || f.flower_code} ({f.qty} tersedia)
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Save BOM Button */}
                      <div className="flex justify-end gap-3 pt-4 border-t border-[#D4859B]/12">
                        <button onClick={() => setModalTab("details")} 
                          className="px-5 py-2.5 rounded-xl text-sm font-body text-[#8A857D] hover:bg-[#FDF8F6] transition-colors">
                          Kembali ke Details
                        </button>
                        <button onClick={saveBom} disabled={saving} data-testid="save-bom-btn"
                          className="px-5 py-2.5 rounded-xl text-sm font-body font-medium bg-seikat-pink text-white hover:bg-seikat-pink/90 transition-colors disabled:opacity-50 flex items-center gap-2">
                          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : "💾 Save BOM"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {modalTab === "details" && (
              <div className="sticky bottom-0 bg-white border-t border-[#D4859B]/12 px-7 py-5 flex justify-end gap-3">
                <button onClick={() => setModal(null)} className="btn-pill">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-pill btn-pill--solid" data-testid="admin-product-save-button">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                  {modal === "create" ? "Create Product" : "Save Changes"}
                </button>
              </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="block font-body text-[10px] tracking-widest uppercase text-[#A8A29A] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors ${
          checked ? "bg-seikat-pink" : "bg-[#D4859B]/25"
        }`}
      >
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`} />
      </div>
      <span className="font-body text-xs text-[#4A453F]">{label}</span>
    </label>
  );
}
